import { Point, BBox } from "geojson";
import { BBox2D } from "./BBox2D";
import { PeliasFeature, PeliasLayer } from "./PeliasFeature";

export interface PeliasQuery {

    /**
     * While rare, API keys used directly on websites or in mobile apps, as recommended by our autocomplete best practices, can be abused by third-parties to make requests that count towards your plan limits.
     * Geocode Earth has several tools available to help you secure your API keys, but we do not disclose them publicly to keep them as effective as possible.
     */
    api_key?: string,
    
    boundary?: PeliasQueryBoundary,

    /**
     * Use the layers parameter to specify only layers you are interested in.
     * Layers can be removed from results by prefixing the layer with a `-`
     * https://geocode.earth/docs/forward/customization/#filter-results-by-data-layer
     */
    layers?: PeliasLayer[]

    /**
     * Use the focus.point parameter to prefer results near a particular coordinate. 
     * Results far away from the focus point may still be returned, but only if they are important (such as a major city or landmark).
     * https://geocode.earth/docs/forward/customization/#use-a-latlon-coordinate-to-prefer-nearby-results
     */
    focus?: PeliasQueryFocus
}

export interface PeliasQueryFocus {
    point?: FocusPoint
}

export interface PeliasQueryBoundary {

    /**
     * Use a country filter to return results only in specified country.
     * Country filters use the boundary.country parameter and require a comma-delimited list of ISO 3166-1 country codes (2 or 3 letter variants).
     * https://geocode.earth/docs/forward/customization/#restrict-results-to-one-or-more-countries
     */
    country?: string | string[];

    /**
     * Filter results to areas smaller than countries, and/or areas with shapes not well represented by rectangular bounding boxes.
     * Only Global IDs starting with `whosonfirst` are suitable for use with `boundary.gid`
     * https://geocode.earth/docs/forward/customization/#restrict-results-by-parent-id
     */
    gid?: string;

    /**
     * Use a bounding box filter to return results only in a specified rectangular area.
     * https://geocode.earth/docs/forward/customization/#restrict-results-to-a-bounding-box
     * Use `bBoxToRect to convert from a GeoJson `BBox` array to `Rect` object
     */
    rect?: Rect;
}

interface FocusPoint {
    lat: number,
    lon: number
}

interface Rect {
    min_lat: number;
    min_lon: number;
    max_lat: number;
    max_lon: number;
}

export const PeliasQuery = {
    getBoundary: (country?: string, ...features: (Partial<PeliasFeature> | undefined)[]): PeliasQueryBoundary => {
        const centroids = features.filter(x => x?.properties?.accuracy === "centroid" && !BBox2D.isPoint(x.bbox));
        const gid = centroids.filter(x => x!.properties?.source === "whosonfirst")[0]?.properties!.gid;
        const bbox = BBox2D.intersection(...centroids.map(PeliasFeature.toBBox));
        const rect = bbox ? bboxToRect(bbox) : undefined;
        return { country, gid, rect };
    },
    
    getFocus: (...features: (Partial<PeliasFeature> | undefined)[]): PeliasQueryFocus | undefined => {
        const feature = features.find(x => x?.geometry?.type === "Point");
        if (!feature) return undefined;
        
        const [ lon, lat ] = (feature.geometry as Point).coordinates
        return { point: { lat, lon } };
    }
}

function bboxToRect(bbox: BBox): Rect {
    return {
        min_lon: bbox[0],
        min_lat: bbox[1],
        max_lon: bbox[2],
        max_lat: bbox[3]
    }
}